import type { HeatwaveState } from './types';

/**
 * Marine heatwaves as Hobday et al. define them (2016, *Progress in
 * Oceanography* 141: 227; categories 2018, *Oceanography* 31(2): 162): five or
 * more days above the 90th percentile for the date, two events with two days
 * or fewer between them being one. The threshold and the record are built by
 * `scripts/data/oisst.ts`, which finds the record's events with these same
 * rules; `systems/climate.ts` follows a sanctuary's day by day (M24).
 */

/** Hobday et al.'s event length and joining gap (definitions). */
export const MIN_EVENT_DAYS = 5;
export const MAX_GAP_DAYS = 2;

/** No heatwave, frozen: shared, it is copied into a world, never put there (review M25). */
export const NO_HEATWAVE: Readonly<HeatwaveState> = Object.freeze({ daysAbove: 0, daysBelow: 0, startEpochDay: null, peakRatio: 0 });

/**
 * A heatwave's state after one more day, as it can be known that day: an
 * event begins on its fifth day above the threshold, dated from the first;
 * goes on while the water stays above, or dips below for two days or fewer;
 * and ends on the third day below. (Hobday et al. join two events across such
 * a gap only when the second also lasts five days, which a day cannot know
 * yet: here any return above within two days goes on with the event.)
 */
export function nextHeatwave(h: HeatwaveState, ratio: number, epochDay: number): HeatwaveState {
  if (ratio > 1) {
    const daysAbove = h.daysAbove + 1;
    if (h.startEpochDay !== null) return { daysAbove, daysBelow: 0, startEpochDay: h.startEpochDay, peakRatio: Math.max(h.peakRatio, ratio) };
    if (daysAbove >= MIN_EVENT_DAYS) return { daysAbove, daysBelow: 0, startEpochDay: epochDay - daysAbove + 1, peakRatio: Math.max(h.peakRatio, ratio) };
    return { daysAbove, daysBelow: 0, startEpochDay: null, peakRatio: Math.max(h.peakRatio, ratio) };
  }
  const daysBelow = h.daysBelow + 1;
  if (h.startEpochDay !== null && daysBelow <= MAX_GAP_DAYS) return { daysAbove: 0, daysBelow, startEpochDay: h.startEpochDay, peakRatio: h.peakRatio };
  return { daysAbove: 0, daysBelow, startEpochDay: null, peakRatio: 0 };
}

/** Hobday et al. (2018)'s categories, by the departure as a multiple of the threshold's: I from 1, II from 2, III from 3, IV from 4; the same in NOAA Coral Reef Watch's (public domain): "≥ (average + diff) and < (average + 2diff) ... Marine Heatwave Category 1 ... and so on", "Category 1 Marine Heatwave (Moderate)" to "Category 4 Marine Heatwave (Extreme)" (its marine heatwave product page). */
export const CATEGORIES = ['I, moderate', 'II, strong', 'III, severe', 'IV, extreme'] as const;

export function categoryOf(ratio: number): string {
  return CATEGORIES[Math.max(0, Math.min(CATEGORIES.length - 1, Math.floor(ratio) - 1))] as string;
}
